import {Injectable, OnDestroy} from '@angular/core';
import {interval} from 'rxjs';
import * as _ from 'lodash';

const source = interval(650);

@Injectable({
  providedIn: 'root'
})
export class NetworkDiagramPathDrawingService implements OnDestroy {

  constructor() {
  }

  subscribe: any;

  ngOnDestroy(): void {
    if (this.subscribe) {
      this.subscribe.unsubscribe();
    }
  }

  clearPath(diagram, data, getClusteredEdgePropertiesByEdges): void {
    if (this.subscribe) {
      this.subscribe.unsubscribe();
    }

    const clusteredEdgesToClear = [];

    const nodesToClear = data.nodes.get(data.currentPathNodes, {
      filter: (item) => (item !== null)
    });

    const edgesToClear = data.edges.get(data.currentPathEdges, {
      filter: (item) => (item !== null)
    });

    nodesToClear.forEach((node) => {
      node.color.border = node.borderColorBeforePathDraw;
      node.borderWidth = node.borderWidthBeforePathDraw;
      delete node.borderColorBeforePathDraw;
      delete node.borderWidthBeforePathDraw;
    });

    edgesToClear.forEach((edge) => {
      edge.color.color = edge.colorBeforePathDraw;
      edge.width = edge.widthBeforePathDraw;
      delete edge.colorBeforePathDraw;
      delete edge.widthBeforePathDraw;
    });

    data.nodes.update(nodesToClear);
    data.edges.update(edgesToClear);

    edgesToClear.forEach((edgeToClear) => {
      const clusteredEdge = diagram.clustering.getClusteredEdges(edgeToClear.id);

      if (clusteredEdge.length > 1) {
        if (clusteredEdgesToClear.indexOf(clusteredEdge[0]) === -1) {
          clusteredEdgesToClear.push(clusteredEdge[0]);
        }
      }
    });

    clusteredEdgesToClear.forEach((clusteredEdgeToClear) => {
      const edgesInCluster = data.edges.get(diagram.clustering.getBaseEdges(clusteredEdgeToClear));
      const prop = getClusteredEdgePropertiesByEdges(edgesInCluster);

      diagram.clustering.updateEdge(clusteredEdgeToClear, {
        color: {color: prop.color},
        width: prop.width,
        title: prop.title
      });
    });

    data.currentPathNodes = [];
    data.currentPathEdges = [];
    data.lastTestRouteState = null;
  }

  drawPath(diagram, data, pathArray, getClusteredEdgePropertiesByEdges): void {
    this.clearPath(diagram, data, getClusteredEdgePropertiesByEdges);

    const dataForDrawing = this.prepareDataForDrawing(diagram, data, pathArray);

    dataForDrawing.mapItemsForUpdate.forEach(dataItem => {
      data.nodes.update(dataItem.nodes);
      data.edges.update(dataItem.edges);
    });

    dataForDrawing.clusteredEdgesToUpdate.forEach((clusteredEdgeToUpdate) => {
      const edgesInCluster = data.edges.get(diagram.clustering.getBaseEdges(clusteredEdgeToUpdate.cluster));
      const prop = getClusteredEdgePropertiesByEdges(edgesInCluster);
      diagram.clustering.updateEdge(clusteredEdgeToUpdate.cluster, {
        color: {color: clusteredEdgeToUpdate.color},
        width: 6,
        title: prop.title
      });
    });
  }

  drawPathAnimated(diagram, data, paths, getClusteredEdgePropertiesByEdges): void {
    this.clearPath(diagram, data, getClusteredEdgePropertiesByEdges);

    const dataForDrawing = this.prepareDataForDrawing(diagram, data, paths);

    this.drawAnimated(diagram, data, dataForDrawing, dataForDrawing.edgesToUpdateInClusters);
  }

  drawAnimated(diagram, data, dataForDrawing, edgesToUpdateInClusters): void {
    let nodeDrawingIndex = 0;
    let edgeDrawingIndex = 0;
    let edgesToUpdate;
    let nodesToUpdate;

    const mapItemsForUpdate = _.cloneDeep(dataForDrawing.mapItemsForUpdate);

    const updateEdge = () => {
      data.edges.update(edgesToUpdate[edgeDrawingIndex]);

      if (edgesToUpdateInClusters[edgesToUpdate[edgeDrawingIndex].id]) {
        diagram.clustering.updateEdge(edgesToUpdate[edgeDrawingIndex].id, {
          color: {color: edgesToUpdateInClusters[edgesToUpdate[edgeDrawingIndex].id].color},
          width: 6
        });
      }

      edgeDrawingIndex++;
      stopDrawing();
    };

    const updateNode = () => {
      data.nodes.update(nodesToUpdate[nodeDrawingIndex]);
      nodeDrawingIndex++;
      stopDrawing();
    };

    const stopDrawing = () => {
      if (nodeDrawingIndex + 1 > nodesToUpdate.length && edgeDrawingIndex + 1 > edgesToUpdate.length) {
        this.subscribe.unsubscribe();
      }
    };

    const drawInterval = () => {
      if (edgeDrawingIndex <= nodeDrawingIndex) {
        updateEdge();
      } else {
        updateNode();
      }
    };

    let nodes = [];
    let edges = [];

    for (let i = 0; i < mapItemsForUpdate.length - 1; i++) {
      nodes = nodes.concat(mapItemsForUpdate[i].nodes);
      edges = edges.concat(mapItemsForUpdate[i].edges);
    }

    data.nodes.update(nodes);
    data.edges.update(edges);

    edgesToUpdate = _.cloneDeep(mapItemsForUpdate[mapItemsForUpdate.length - 1].edges);
    nodesToUpdate = _.cloneDeep(mapItemsForUpdate[mapItemsForUpdate.length - 1].nodes);

    updateEdge();
    this.subscribe = source.subscribe(drawInterval);
  }

  prepareDataForDrawing(diagram, data, paths): any {
    const clusteredEdgesToUpdate = [];
    const edgesToUpdateInClusters = {};

    let nodesToUpdateFinal = [];
    let edgesToUpdateFinal = [];

    data.currentPathNodes = [];
    data.currentPathEdges = [];
    data.lastTestRouteState = _.cloneDeep(paths);

    const nodesSaved = [];
    const edgesSaved = [];

    const mapItemsForUpdate = [];

    paths.forEach(path => {
      path.nodes.forEach(node => {
        if (data.currentPathNodes.indexOf(node) === -1) {
          data.currentPathNodes.push(node);
        }
      });

      path.edges.forEach(edge => {
        if (data.currentPathEdges.indexOf(edge) === -1) {
          data.currentPathEdges.push(edge);
        }
      });

      const nodesToUpdate = data.nodes.get(path.nodes, {
        filter: (item) => (item !== null)
      });

      const edgesToUpdate = data.edges.get(path.edges, {
        filter: (item) => (item !== null)
      });

      for (const node of nodesToUpdate) {
        if (nodesSaved.indexOf(node.id) === -1) {
          node.borderColorBeforePathDraw = _.cloneDeep(node.color.border);
          node.borderWidthBeforePathDraw = _.cloneDeep(node.borderWidth);
          nodesSaved.push(node.id);
        }

        node.x = undefined;
        node.y = undefined;
        node.color.border = path.color;
        node.borderWidth = 6;
      }

      for (const edge of edgesToUpdate) {
        const clusteredEdge = diagram.clustering.getClusteredEdges(edge.id);

        if (edgesSaved.indexOf(edge.id) === -1) {
          edge.colorBeforePathDraw = _.cloneDeep(edge.color.color);
          edge.widthBeforePathDraw = _.cloneDeep(edge.width);
          edgesSaved.push(edge.id);
        }

        if (clusteredEdge.length > 1) {
          edgesToUpdateInClusters[edge.id] = {
            cluster: clusteredEdge[0],
            color: path.color
          };

          if (clusteredEdgesToUpdate.indexOf(clusteredEdge[0]) === -1) {
            clusteredEdgesToUpdate.push({
              cluster: clusteredEdge[0],
              color: path.color
            });
          }
        }

        edge.color.color = path.color;
        edge.width = 6;
      }

      nodesToUpdateFinal = nodesToUpdateFinal.concat(_.cloneDeep(nodesToUpdate));
      edgesToUpdateFinal = edgesToUpdateFinal.concat(_.cloneDeep(edgesToUpdate));

      mapItemsForUpdate.push({
        nodes: nodesToUpdate,
        edges: edgesToUpdate
      });
    });

    return {
      mapItemsForUpdate,
      nodesToUpdate: nodesToUpdateFinal,
      edgesToUpdate: edgesToUpdateFinal,
      edgesToUpdateInClusters,
      clusteredEdgesToUpdate
    };
  }
}
